import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import { LayoutGroup, motion } from "framer-motion";
import { Sidebar } from "@/components/app/Sidebar";
import { GlobalSearch } from "@/components/app/GlobalSearch";
import { NotificationsBell } from "@/components/app/NotificationsBell";
import { GettingStartedRing } from "@/components/app/GettingStarted";
import { useNotificationPoll } from "@/lib/useNotificationPoll";
import { UserMenu } from "@/components/app/UserMenu";
import { startTour } from "@/components/app/tour";
import { EASE } from "@/components/motion/Motion";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { PageTour } from "@/components/app/PageTour";
import { DpoBanner } from "@/components/app/DpoBanner";
import { EmailAlertBanner } from "@/components/app/EmailAlerts";
import { Menu } from "lucide-react";

function useTour() {
  const { user, setUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const replay = params.get("tour") === "1";
  const started = useRef(false);
  const seen = useRef(false);

  useEffect(() => {
    if (!user || (!replay && (user.tour_completed || seen.current)) || started.current) return;
    const t = setTimeout(() => {
      started.current = true;
      startTour(user.role, async () => {
        started.current = false;
        seen.current = true;
        if (replay) setParams((p) => { p.delete("tour"); return p; }, { replace: true });
        if (!user.tour_completed) {
          const { data } = await api.post("/auth/tour-complete");
          setUser(data);
        }
      });
    }, 700);
    return () => clearTimeout(t);
  }, [user, replay, setParams, setUser]);
}

function MobileDrawer({ path }) {
  const [open, setOpen] = useState(false);
  useEffect(() => { setOpen(false); }, [path]);
  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener("tl-drawer", show);
    return () => window.removeEventListener("tl-drawer", show);
  }, []);
  useEffect(() => {
    if (!open) return;
    const k = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Menu" data-testid="mobile-drawer">
      <div className="absolute inset-0 bg-[#1B1E1A]/35" onClick={() => setOpen(false)} data-testid="mobile-drawer-backdrop" />
      <Sidebar collapsed={false} onToggle={() => setOpen(false)} drawer className="absolute inset-y-0 left-0 h-[100dvh] overflow-y-auto shadow-[0_24px_64px_-12px_rgba(27,30,26,0.28)]" />
    </div>
  );
}

const sectionKey = (p) => p.replace(/^(\/app\/(jobs|candidates)\/[^/]+)\/[^/]+$/, "$1").replace(/^(\/app\/settings)\/.*$/, "$1");

export default function AppShell() {
  useNotificationPoll();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("tl_sidebar") === "1");
  const loc = useLocation();
  useTour();

  const toggle = () => {
    localStorage.setItem("tl_sidebar", collapsed ? "0" : "1");
    setCollapsed(!collapsed);
  };

  return (
    <div className="flex min-h-screen bg-canvas" data-testid="app-shell">
      <Sidebar collapsed={collapsed} onToggle={toggle} className="max-lg:hidden" />
      <MobileDrawer path={loc.pathname} />
      <div className="relative flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-line bg-canvas px-6 max-lg:h-14 max-lg:gap-2 max-sm:px-3">
          <button onClick={() => window.dispatchEvent(new Event("tl-drawer"))} aria-label="Open menu" className="-ml-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-[4px] text-ink hover:bg-[#EFEBE3] lg:hidden" data-testid="mobile-menu-btn"><Menu size={20} /></button>
          <GlobalSearch />
          <div className="ml-auto flex shrink-0 items-center gap-2 max-sm:gap-1">
            <GettingStartedRing />
            <PageTour />
            <NotificationsBell />
            <UserMenu />
          </div>
        </header>
        <DpoBanner />
        <EmailAlertBanner />
        <LayoutGroup>
          <motion.main key={sectionKey(loc.pathname)} data-testid="route-view"
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.24, ease: EASE }}
            className="mx-auto w-full max-w-[1440px] flex-1 px-6 pb-12 pt-8 lg:px-10 max-sm:px-4 max-sm:pt-5">
            <Outlet />
          </motion.main>
        </LayoutGroup>
      </div>
    </div>
  );
}
